import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  CircularProgress,
  Container,
  Paper,
  Typography,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CloudQueueIcon from "@mui/icons-material/CloudQueue";
import { alpha } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";

import DashboardLayout from "../../../layouts/DashboardLayout";
import PageHeader from "../../../components/layout/PageHeader";
import { listarCuentasFlespi } from "../services/flespiService";

const menu = [
  {
    title: "Flespi 1",
    sections: [
      {
        title: "YPFB Internacional",
        color: "#1f7f75",
        bg: "#f0fdfa",
        items: [
          ["General", "flespi_internacional", "#df3347"],
          ["YPFB Internacional 2", "flespi_internacional_2"],
          ["YPFB Internacional 3", "flespi_internacional_3"],
          ["YPFB Internacional 4", "flespi_internacional_4"],
          ["YPFB Internacional 5", "flespi_internacional_5"],
          ["Híbrido Internacional .COM.BO", "flespi_hibrido_internacional_bo"],
        ],
      },
      {
        title: "YPFB Nacional",
        color: "#15803d",
        bg: "#f0fdf4",
        items: [
          ["YPFB Nacional", "flespi_nacional"],
          ["Híbrido Nacional .COM.BO", "flespi_hibrido_nacional_bo"],
        ],
      },
      {
        title: "trsrastreo.com.bo",
        color: "#18bfdf",
        bg: "#f0f9ff",
        items: [
          ["trsrastreo.COM.BO", "flespi_com_bo"],
          ["DUAL .COM.BO", "flespi_dual_com_bo"],
        ],
      },
    ],
    origen: "flespi",
  },
  {
    title: "Flespi 2",
    sections: [
      {
        title: "YPFB Internacional",
        color: "#1f7f75",
        bg: "#f0fdfa",
        items: [
          ["General", "admin_flespi_2", "#df3347"],
          ["YPFB Internacional", "flespi_internacional"],
          ["Híbrido Internacional .COM.BO", "flespi_hibrido_internacional_bo"],
        ],
      },
      {
        title: "YPFB Nacional",
        color: "#15803d",
        bg: "#f0fdf4",
        items: [
          ["YPFB Nacional", "flespi_nacional"],
          ["Híbrido Nacional .COM.BO", "flespi_hibrido_nacional_bo"],
        ],
      },
      {
        title: "trsrastreo.com.bo",
        color: "#18bfdf",
        bg: "#f0f9ff",
        items: [["trsrastreo.COM.BO", "flespi_com_bo"]],
      },
    ],
    origen: "flespi2",
  },
];

const getRows = (response) => {
  const body = response?.data;
  if (Array.isArray(body)) return body;
  if (Array.isArray(body?.data)) return body.data;
  return [];
};

export default function FlespiMainPage() {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    listarCuentasFlespi()
      .then((response) => {
        if (active) setAccounts(getRows(response));
      })
      .catch((requestError) => {
        if (active) {
          setError(
            requestError?.response?.data?.error ||
              requestError?.message ||
              "No se pudieron cargar las cuentas Flespi."
          );
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, []);

  const accountSet = useMemo(
    () => new Set(accounts.map((item) => `${item.origen}|${item.nombre}`)),
    [accounts]
  );

  const accountSlugMap = useMemo(
    () => new Map(accounts.map((item) => [`${item.origen}|${item.nombre}`, item.slug])),
    [accounts]
  );

  const openAccount = (origen, tipo) => {
    const slug = accountSlugMap.get(`${origen}|${tipo}`);
    if (slug) {
      navigate(`/flespi/dispositivos/${encodeURIComponent(slug)}`);
      return;
    }
    navigate(`/flespi/dispositivos?origen=${encodeURIComponent(origen)}&tipo=${encodeURIComponent(tipo)}`);
  };

  return (
    <DashboardLayout>
      <PageHeader
        title="Flespi"
        subtitle="Seleccione la cuenta que desea administrar"
        backTo="/dashboard"
      />

      <Box
        sx={{
          bgcolor: (theme) => theme.palette.background.default,
          minHeight: "calc(100vh - 110px)",
          py: 1.5,
        }}
      >
        <Container maxWidth="xl">
          {error && <Alert severity="error" sx={{ mb: 1.5 }}>{String(error)}</Alert>}

          {loading ? (
            <Box sx={{ py: 8, display: "grid", placeItems: "center" }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <Box sx={{ display: "grid", gap: 1.5 }}>
              {menu.map((server) => (
                <Paper key={server.origen} variant="outlined" sx={{ p: 1.25, borderRadius: 1.5 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                    <CloudQueueIcon sx={{ color: "primary.main" }} />
                    <Typography sx={{ fontWeight: 900, fontSize: "1rem" }}>
                      {server.title}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(2, minmax(0, 1fr))",
                        lg: "repeat(3, minmax(0, 1fr))",
                      },
                      gap: 1,
                    }}
                  >
                    {server.sections.map((section) => (
                      <Box
                        key={section.title}
                        sx={{
                          border: "1px solid",
                          borderColor: "divider",
                          borderTop: `3px solid ${section.color}`,
                          bgcolor: (theme) =>
                            theme.palette.mode === "dark"
                              ? alpha(section.color, 0.14)
                              : section.bg,
                          p: 1,
                          minWidth: 0,
                          boxShadow: (theme) =>
                            theme.palette.mode === "dark"
                              ? `inset 0 0 0 1px ${alpha(section.color, 0.1)}`
                              : "none",
                        }}
                      >
                        <Typography sx={{ fontWeight: 850, fontSize: "0.82rem", mb: 0.75 }}>
                          {section.title}
                        </Typography>
                        <Box sx={{ display: "grid", gap: 0.5 }}>
                          {section.items.map(([label, tipo, itemColor]) => {
                            const available = accountSet.has(`${server.origen}|${tipo}`);
                            const buttonColor = itemColor || section.color;
                            return (
                              <Box
                                component="button"
                                type="button"
                                key={`${server.origen}-${tipo}`}
                                disabled={!available}
                                onClick={() => openAccount(server.origen, tipo)}
                                sx={{
                                  border: "1px solid",
                                  borderColor: (theme) =>
                                    available
                                      ? alpha(buttonColor, theme.palette.mode === "dark" ? 0.75 : 1)
                                      : theme.palette.divider,
                                  bgcolor: (theme) => {
                                    if (!available) return theme.palette.action.disabledBackground;
                                    return theme.palette.mode === "dark"
                                      ? alpha(buttonColor, 0.72)
                                      : buttonColor;
                                  },
                                  minHeight: 34,
                                  px: 1,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  gap: 1,
                                  color: available ? "#fff" : "text.disabled",
                                  fontFamily: "inherit",
                                  fontSize: "0.76rem",
                                  fontWeight: 700,
                                  cursor: available ? "pointer" : "not-allowed",
                                  textAlign: "left",
                                  boxShadow: available
                                    ? (theme) =>
                                        theme.palette.mode === "dark"
                                          ? `0 1px 6px ${alpha(buttonColor, 0.2)}`
                                          : "0 1px 2px rgba(15, 23, 42, 0.14)"
                                    : "none",
                                  transition: "filter 140ms ease, transform 140ms ease, box-shadow 140ms ease",
                                  "&:hover": available
                                    ? {
                                        filter: "brightness(0.92)",
                                        transform: "translateY(-1px)",
                                        boxShadow: (theme) =>
                                          theme.palette.mode === "dark"
                                            ? `0 4px 12px ${alpha(buttonColor, 0.24)}`
                                            : "0 3px 8px rgba(15, 23, 42, 0.18)",
                                      }
                                    : {},
                                }}
                              >
                                <span>{label}</span>
                                <ArrowForwardIcon sx={{ fontSize: 16, flex: "0 0 auto" }} />
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
        </Container>
      </Box>
    </DashboardLayout>
  );
}
